import { useState, ReactNode, ComponentType } from 'react';
import cn from 'classnames';
import SearchInputContainer from '@/creditflowresearch/ui/common/components/organisms/HeaderSearchInput';
import Grid from '@creditflowresearch/uilib/Grid';
import GridItem from '@creditflowresearch/uilib/GridItem';
import Flex from '@creditflowresearch/uilib/Flex';
import ExpandedContent from '@creditflowresearch/uilib/ExpandedContent';
import MobileLogo from '@/creditflowresearch/ui/common/components/molecules/MobileLogo';
import SearchWrapper from '@/creditflowresearch/ui/common/components/organisms/SearchWrapper';
import UserProfileMenuTrigger
  from '@/creditflowresearch/ui/common/components/organisms/UserProfileMenuTrigger';
import MobileNavigationMenu
  from '@/creditflowresearch/ui/common/components/organisms/MobileNavigationMenu';
import { UserProfileMenuTemplateMobile } from '@/creditflowresearch/ui/common/components/templates/UserProfileMenuTemplate';
import { panelManager } from '@creditflowresearch/uilib/PanelControl/panelManager';
import FeatureToggle from '@framework/ui/Organisms/FeatureToggle/Components';
import { ASK_AI } from '@creditflowresearch/config/featureToggle';
import { AskAiMobileButton } from '@/creditflowresearch/ui/common/components/molecules/AskAiButton';
import useGetHomePageUrl from '@/creditflowresearch/application/hooks/useGetHomePageUrl';

import styles from './headerLayoutMobile.module.scss';

interface IHeaderLayoutMobileProps {
  logo?: ReactNode,
  searchComponent?: ReactNode,
}

const HeaderLayoutMobile = ({
  logo,
  searchComponent = <SearchInputContainer />,
}: IHeaderLayoutMobileProps) => {
  const [ActiveMenuComponent, setActiveMenuComponent] = useState<ComponentType>(null);
  const homePageUrl = useGetHomePageUrl();

  const isUserProfileMenu = ActiveMenuComponent === UserProfileMenuTemplateMobile;

  const toggleMenuComponent = (component: ComponentType) => {
    setActiveMenuComponent((prev) => prev === component ? null : component);
  };

  panelManager.registerCloseMobileMenu(() => {
    setActiveMenuComponent(null);
  });

  return (
    <SearchWrapper>
      <Grid
        templateRows={ ActiveMenuComponent ? 'auto auto 1fr' : 'auto auto' }
        templateColumns="1fr"
        gap={ 10 }
        className={ cn(styles.headerContainer, ActiveMenuComponent && styles.expandedHeader) }
        data-test="mobileHeader"
      >
        <GridItem className={ styles.topRow }>
          <Grid
            dataTest="mobileHeaderTopRow"
            templateColumns="1fr 1fr"
            gap={ 0 }
          >
            <GridItem>
              <Flex align="center" justify="flex-start" className={ styles.fullSpace }>
                { logo || (
                  <MobileLogo
                    url={ homePageUrl }
                    isMenuOpen={ ActiveMenuComponent === MobileNavigationMenu }
                    onMenuToggle={ () => toggleMenuComponent(MobileNavigationMenu) }
                  />
                ) }
              </Flex>
            </GridItem>

            <GridItem className={ styles.menuGridItem }>
              <Flex align="center" justify="flex-end" gap={ 10 }>
                <FeatureToggle featureName={ ASK_AI }>
                  <AskAiMobileButton />
                </FeatureToggle>
                <UserProfileMenuTrigger
                  isActive={ ActiveMenuComponent === UserProfileMenuTemplateMobile }
                  onClick={ () => toggleMenuComponent(UserProfileMenuTemplateMobile) }
                />
              </Flex>
            </GridItem>
          </Grid>
        </GridItem>

        <GridItem className={ styles.bottomRow }>
          <Flex
            align="center"
            justify="center"
            dataTest="mobileSearchContainer"
          >
            { searchComponent }
          </Flex>
        </GridItem>

        { ActiveMenuComponent && (
          <GridItem className={ styles.expandedContent }>
            <ExpandedContent
              key={ isUserProfileMenu ? 'userProfileMenu' : 'navigationMenu' }
              dataTest="mobileExpandedContent"
            >
              { isUserProfileMenu ? (
                <UserProfileMenuTemplateMobile onClose={ () => setActiveMenuComponent(null) } />
              ) : (
                <ActiveMenuComponent />
              ) }
            </ExpandedContent>
          </GridItem>
        ) }
      </Grid>
    </SearchWrapper>
  );
};

export default HeaderLayoutMobile;
